import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { artworkService, ArtworkResponse } from '../../services/artworkService';
import { EnhancedArtworkCard } from '../gallery/EnhancedArtworkCard';
import { ArtworkLightbox } from '../ui/ArtworkLightbox';

const Section = styled.section`
  background: ${({ theme }) => theme.colors.gallery.surface};
  padding: clamp(1rem, 2vw, 1.5rem) 0;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const Title = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.section};
  font-weight: 600;

  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

const CountPill = styled.span`
  padding: 0.2rem 0.6rem;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: 9999px;
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  color: ${({ theme }) => theme.colors.gallery.text.muted};
  font-size: 0.75rem;
  font-weight: 500;
`;

const SeeAll = styled(Link)`
  margin-left: auto;
  color: ${({ theme }) => theme.colors.gallery.accent.primary};
  font-size: 0.875rem;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.gallery.accent.hover};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 2px;
  }
`;

const MobileScrollTrack = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 768px) {
    display: none;
  }
`;

const DesktopGrid = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (min-width: 1025px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
`;

const MobileCard = styled(EnhancedArtworkCard)`
  flex: 0 0 176px;
`;

const DesktopCard = styled(EnhancedArtworkCard)``;

const Status = styled.div`
  display: grid;
  gap: 0.75rem;
  min-height: 120px;
  place-items: center;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  text-align: center;
`;

const RetryButton = styled.button`
  min-height: 44px;
  padding: 0.5rem 0.9rem;
  border: 1px solid ${({ theme }) => theme.colors.semantic.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.semantic.surfaceRaised};
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 2px;
  }
`;

const shimmerStyle = `
  background: linear-gradient(90deg, #20201d 25%, #2b2b27 50%, #20201d 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  @keyframes shimmer {
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
  }
`;

const MobileSkeletonCard = styled.div`
  ${shimmerStyle}
  flex: 0 0 176px;
  aspect-ratio: 4 / 5;
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
`;

const DesktopSkeletonCard = styled.div`
  ${shimmerStyle}
  aspect-ratio: 4 / 5;
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
`;

function RecentWorksState({
  message,
  onRetry,
  isRetrying = false,
}: {
  message: string;
  onRetry?: () => void;
  isRetrying?: boolean;
}) {
  return (
    <Status role={onRetry ? 'alert' : 'status'}>
      <span>{message}</span>
      {onRetry && (
        <RetryButton type="button" onClick={onRetry} disabled={isRetrying}>
          {isRetrying ? 'Trying again…' : 'Try again'}
        </RetryButton>
      )}
    </Status>
  );
}

export function RecentWorksStrip() {
  const query = useQuery({
    queryKey: ['recent-artworks-strip'],
    queryFn: () => artworkService.getArtworks(1, 16),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    retry: false,
  });
  const publicDataReady = query.isFetchedAfterMount && query.isSuccess && !query.isError && !query.isRefetching && query.fetchStatus !== 'paused';
  const artworks: ArtworkResponse[] = publicDataReady ? (query.data?.artworks || []) : [];
  const total = publicDataReady ? (query.data?.total ?? 0) : 0;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState<ArtworkResponse | null>(null);
  const [currentArtworkIndex, setCurrentArtworkIndex] = useState(0);
  const [viewerArtworks, setViewerArtworks] = useState<ArtworkResponse[]>([]);
  const [viewerOpener, setViewerOpener] = useState<HTMLElement | null>(null);

  const openLightbox = (artwork: ArtworkResponse, activatingElement?: HTMLElement) => {
    const index = artworks.findIndex(({ artwork_id }) => artwork_id === artwork.artwork_id);
    setViewerArtworks(artworks.slice());
    setSelectedArtwork(artwork);
    setCurrentArtworkIndex(Math.max(index, 0));
    setViewerOpener(activatingElement ?? null);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedArtwork(null);
    setViewerArtworks([]);
    setViewerOpener(null);
  };

  useEffect(() => {
    if (query.isRefetching || !publicDataReady) closeLightbox();
  }, [publicDataReady, query.isRefetching]);

  const navigateLightbox = (direction: 'prev' | 'next') => {
    const nextIndex = direction === 'prev' ? currentArtworkIndex - 1 : currentArtworkIndex + 1;
    if (nextIndex >= 0 && nextIndex < viewerArtworks.length) {
      setCurrentArtworkIndex(nextIndex);
      setSelectedArtwork(viewerArtworks[nextIndex]);
    }
  };

  return (
    <Section>
      <Inner>
        <Header>
          <Title>Recent Works</Title>
          {total > 0 && <CountPill>{total}</CountPill>}
          <SeeAll to="/gallery">See all →</SeeAll>
        </Header>

        {(!publicDataReady && query.isFetching) && (
          <>
            <MobileScrollTrack>
              {Array.from({ length: 8 }, (_, index) => <MobileSkeletonCard key={index} />)}
            </MobileScrollTrack>
            <DesktopGrid>
              {Array.from({ length: 8 }, (_, index) => <DesktopSkeletonCard key={index} />)}
            </DesktopGrid>
          </>
        )}
        {query.fetchStatus === 'paused' && !query.isFetching && (
          <RecentWorksState message="Recent works are waiting for a network connection." onRetry={() => { void query.refetch(); }} isRetrying={query.isFetching} />
        )}
        {query.isError && !query.isFetching && (
          <RecentWorksState
            message="Recent works are temporarily unavailable."
            onRetry={() => { void query.refetch(); }}
            isRetrying={query.isFetching}
          />
        )}
        {publicDataReady && artworks.length === 0 && (
          <RecentWorksState message="There are no recent works to show yet." />
        )}
        {publicDataReady && artworks.length > 0 && (
          <>
            <MobileScrollTrack>
              {artworks.map((artwork) => (
                <MobileCard
                  key={artwork.artwork_id}
                  artwork={artwork}
                  aspectRatio={4 / 5}
                  onClick={openLightbox}
                />
              ))}
            </MobileScrollTrack>
            <DesktopGrid>
              {artworks.map((artwork) => (
                <DesktopCard
                  key={artwork.artwork_id}
                  artwork={artwork}
                  aspectRatio={4 / 5}
                  onClick={openLightbox}
                />
              ))}
            </DesktopGrid>
          </>
        )}

        <ArtworkLightbox
          artwork={selectedArtwork}
          artworks={viewerArtworks}
          currentIndex={currentArtworkIndex}
          isOpen={lightboxOpen}
          onClose={closeLightbox}
          onNavigate={navigateLightbox}
          activatingElement={viewerOpener}
        />
      </Inner>
    </Section>
  );
}
